import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { formatPrice } from '../../utils/formatHelper';
import Select from 'react-select/async';
import ActionDrawer from '../components/shared/ActionDrawer';
import { showSuccess, showError, showServerError } from '../../utils/toastHelper';

function CreateOrderModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [stockErrors, setStockErrors] = useState([]);
  const [eligibilityCheck, setEligibilityCheck] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedSubscription(null);
      setOrderNotes('');
      setErrorDetails('');
      setStockErrors([]);
      setEligibilityCheck(null);
    }
  }, [isOpen]);

  const loadSubscriptions = async (inputValue) => {
    try {
      const queryParams = new URLSearchParams({
        search: inputValue || '',
        page: 1,
        limit: 100,
        status: 'active'
      });

      const response = await apiRequest(`/subscriptions?${queryParams.toString()}`);

      if (response.success) {
        return response.subscriptions.map(subscription => ({
          value: subscription.id,
          label: `${subscription.customer.name} - ${subscription.plan.name} (${subscription.interval})`,
          subscription: subscription
        }));
      }
      return [];
    } catch (error) {
      console.error('Abonelikler yüklenirken hata:', error);
      showError('Abonelikler yüklenirken bir hata oluştu');
      return [];
    }
  };

  const handleSubscriptionChange = (selectedOption) => {
    setSelectedSubscription(selectedOption);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!selectedSubscription) {
      showError('Lütfen bir abonelik seçin');
      return;
    }

    try {
      setLoading(true);
      setErrorDetails('');
      setStockErrors([]);
      setEligibilityCheck(null);

      const response = await apiRequest('/orders/manual', {
        method: 'POST',
        data: {
          subscriptionId: selectedSubscription.value,
          notes: orderNotes.trim() || null
        }
      });

      if (response.success) {
        showSuccess(response.message || 'Sipariş başarıyla oluşturuldu');
        onSuccess && onSuccess();
      } else {
        if (response.stockErrors && response.stockErrors.length > 0) {
          setStockErrors(response.stockErrors);
          setErrorDetails(response.message);
        } else if (response.eligibilityCheck) {
          setEligibilityCheck(response.eligibilityCheck);
          setErrorDetails(response.message);
        } else {
          setErrorDetails(response.message || 'İşlem sırasında bir hata oluştu');
        }
        showError(response.message || 'İşlem sırasında bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to create order:', error);
      if (error.data?.details) setErrorDetails(error.data.details);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'İşlem sırasında bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-light" onClick={onClose}>
        İptal
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading || !selectedSubscription}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Oluşturuluyor...
          </>
        ) : (
          'Sipariş Oluştur'
        )}
      </button>
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Manuel Sipariş Oluştur"
      width="600px"
      footer={footer}
    >
      <div className="order-form-content">
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label text-muted small fw-medium">Abonelik Seçin <span className="text-danger">*</span></label>
            <Select
              value={selectedSubscription}
              onChange={handleSubscriptionChange}
              loadOptions={loadSubscriptions}
              defaultOptions
              cacheOptions
              placeholder="Abonelik ara..."
              noOptionsMessage={() => "Abonelik bulunamadı"}
              loadingMessage={() => "Yükleniyor..."}
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
            />
          </div>

          {selectedSubscription && (
            <div className="col-12">
              <div className="card bg-light border-0 shadow-sm">
                <div className="card-body p-3">
                  <h6 className="card-title mb-3 fw-bold">Abonelik Detayları</h6>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <small className="text-muted">Müşteri:</small>
                      <div className="fw-medium">{selectedSubscription.subscription.customer.name}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">E-posta:</small>
                      <div className="fw-medium">{selectedSubscription.subscription.customer.email}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Plan:</small>
                      <div className="fw-medium">{selectedSubscription.subscription.plan.name}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Teslimat Sıklığı:</small>
                      <div className="fw-medium">
                        {selectedSubscription.subscription.interval === 'MONTHLY' ? 'Aylık' : '2 Haftalık'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Tutar:</small>
                      <div className="fw-medium text-orange">
                        {formatPrice(selectedSubscription.subscription.amount)}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Ürün Sayısı:</small>
                      <div className="fw-medium">{selectedSubscription.subscription.products.length} ürün</div>
                    </div>
                  </div>

                  {selectedSubscription.subscription.products.length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted">Ürünler:</small>
                      <div className="mt-2">
                        {selectedSubscription.subscription.products.map((product) => (
                          <div key={product.id} className="d-flex align-items-center mb-2">
                            {product.mainImage && (
                              <img
                                src={product.mainImage}
                                alt={product.name}
                                className="product-image me-2"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-medium small">{product.name}</div>
                              <div className="text-muted small">
                                {product.quantity} adet × {formatPrice(product.price)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="col-12">
            <label className="form-label text-muted small fw-medium">Sipariş Notları</label>
            <textarea
              className="form-control"
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Opsiyonel sipariş notları..."
              rows="3"
            ></textarea>
          </div>

          {errorDetails && (
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                <div className="d-flex align-items-start">
                  <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <strong>Hata:</strong>
                    <p className="mb-2 mt-1">{errorDetails}</p>

                    {stockErrors.length > 0 && (
                      <div className="mt-3">
                        <strong>Stok Sorunları:</strong>
                        <div className="mt-2">
                          {stockErrors.map((error, index) => (
                            <div key={index} className="d-flex align-items-center mb-2 p-2 bg-light rounded shadow-sm">
                              <div className="flex-grow-1">
                                <div className="fw-medium small">{error.productName}</div>
                                <div className="text-muted small">
                                  İstenen: {error.requested} adet | Mevcut: {error.available} adet
                                </div>
                              </div>
                              <span className="badge bg-danger small">{error.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {eligibilityCheck && (
                      <div className="mt-3">
                        <strong>Uygunluk Kontrolü:</strong>
                        <div className="mt-2">
                          {eligibilityCheck.reasons.map((reason, index) => (
                            <div key={index} className="d-flex align-items-center mb-2 p-2 bg-light rounded shadow-sm">
                              <i className="fas fa-times-circle text-danger me-2"></i>
                              <span className="small">{reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .react-select-container { width: 100%; }
        .react-select__control {
          min-height: 38px;
          border-color: #ced4da;
        }
        .react-select__control--is-focused {
          border-color: var(--kito-orange) !important;
          box-shadow: 0 0 0 0.25rem rgba(232, 108, 58, 0.25) !important;
        }
        .product-image {
          width: 32px;
          height: 32px;
          object-fit: cover;
          border-radius: 4px;
          border: 1px solid #dee2e6;
        }
        @media (max-width: 767px) {
          .product-image { width: 24px; height: 24px; }
        }
      `}</style>
    </ActionDrawer>
  );
}

export default CreateOrderModal; 